import {ReactNode, useMemo} from 'react';
import {Code, Heading, Link, List, Text, VStack} from '@chakra-ui/react';
import {MarkdownBlock, MarkdownInline, parseMarkdown} from '../releaseNotes/markdown';

/** Renders the Markdown subset of `domain/markdown.ts` with Chakra's typography. */
export function Markdown({source}: { source: string }) {
    const blocks = useMemo(() => parseMarkdown(source), [source]);
    return <VStack align="stretch" gap={3}>{blocks.map(renderBlock)}</VStack>;
}

function renderBlock(block: MarkdownBlock, index: number): ReactNode {
    switch (block.type) {
        case 'heading':
            return block.level <= 2
                ? <Heading key={index} as="h4" size="sm">{renderInline(block.children)}</Heading>
                : <Heading key={index} as="h5" size="xs">{renderInline(block.children)}</Heading>;
        case 'paragraph':
            return <Text key={index}>{renderInline(block.children)}</Text>;
        case 'list':
            return (
                <List.Root key={index} as={block.ordered ? 'ol' : 'ul'} gap={1}>
                    {block.items.map((item, i) => <List.Item key={i}>{renderInline(item)}</List.Item>)}
                </List.Root>
            );
    }
}

function renderInline(nodes: MarkdownInline[]): ReactNode[] {
    return nodes.map(renderInlineNode);
}

function renderInlineNode(node: MarkdownInline, index: number): ReactNode {
    switch (node.type) {
        case 'text':
            return node.text;
        case 'strong':
            return <strong key={index}>{renderInline(node.children)}</strong>;
        case 'em':
            return <em key={index}>{renderInline(node.children)}</em>;
        case 'code':
            return <Code key={index}>{node.text}</Code>;
        case 'link':
            return <Link key={index} href={node.href} target="_blank" rel="noopener noreferrer" color="blue.500">{renderInline(node.children)}</Link>;
    }
}
